import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { Send, MapPin, Phone, Mail, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { useSiteSettings } from "@/data/useSiteSettings";
import useRecaptcha from "@/hooks/useRecaptcha";
import Honeypot from "@/components/Honeypot";
import {
  CONTACT_PAGE_DEFAULT,
  CONTACT_INFO_DEFAULT,
  mergeSection,
} from "@/data/homepageSectionDefaults";

// Extracted reusable contact form + info panel. Rendered by both the
// standalone ContactPage and the CMS `contact_form` block. Copy comes from
// site settings (contact_page / contact_info) so it stays editable.

function formatPhoneHref(phone) {
  if (!phone) return "";
  return `tel:${phone.replace(/[^+0-9]/g, "")}`;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const settings = useSiteSettings();
  const copy = mergeSection(CONTACT_PAGE_DEFAULT, settings?.contact_page);
  const contactInfo = { ...CONTACT_INFO_DEFAULT, ...(settings?.contact_info || {}) };

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    phone: "",
    role: "",
    referral_source: "",
    other_info: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const recaptcha = useRecaptcha();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.email ||
      !formData.company ||
      !formData.phone ||
      !formData.role ||
      !formData.referral_source
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const token = await recaptcha.execute("contact_submit");
      await axios.post(`${API}/contact`, {
        ...formData,
        source_page: typeof window !== "undefined" ? window.location.pathname : "",
        recaptcha_token: token,
        website_url: honeypot,
      });
      setSubmitted(true);
      toast.success("Demo request submitted successfully!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 bg-white">
        <Toaster position="top-right" richColors />
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h1
              className="text-3xl font-bold text-slate-900"
              data-testid="contact-success-title"
            >
              {copy.success_title}
            </h1>
            <p className="mt-4 text-base text-slate-500 leading-relaxed">
              {copy.success_message}
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 bg-white" data-testid="contact-form-section">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left - Info */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
                {copy.hero_lead}{" "}
                <span className="text-sky-600">{copy.hero_highlight}</span>
              </h2>
              <p className="mt-5 text-base text-slate-500 leading-relaxed">
                {copy.intro}
              </p>

              <div className="mt-10 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <Mail className="h-4 w-4 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email</p>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      data-testid="contact-info-email"
                      className="text-sm text-slate-500 hover:text-sky-600 transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <Phone className="h-4 w-4 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Phone</p>
                    <a
                      href={formatPhoneHref(contactInfo.phone)}
                      data-testid="contact-info-phone"
                      className="text-sm text-slate-500 hover:text-sky-600 transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Location</p>
                    <p
                      className="text-sm text-slate-500 whitespace-pre-line"
                      data-testid="contact-info-address"
                    >
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right - Form */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 shadow-sm"
              data-testid="contact-form"
            >
              <Honeypot value={honeypot} onChange={setHoneypot} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
                    First Name *
                  </Label>
                  <Input
                    id="first_name"
                    data-testid="contact-first-name"
                    value={formData.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    placeholder="John"
                    className="mt-1.5 border-slate-300"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
                    Last Name *
                  </Label>
                  <Input
                    id="last_name"
                    data-testid="contact-last-name"
                    value={formData.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    placeholder="Doe"
                    className="mt-1.5 border-slate-300"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="john@company.com"
                  className="mt-1.5 border-slate-300"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                    Company *
                  </Label>
                  <Input
                    id="company"
                    data-testid="contact-company"
                    value={formData.company}
                    onChange={(e) => handleChange("company", e.target.value)}
                    placeholder="Acme Corp"
                    className="mt-1.5 border-slate-300"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1.5 border-slate-300"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(v) => handleChange("role", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-role-select"
                      className="mt-1.5 border-slate-300"
                    >
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Owner">Owner</SelectItem>
                      <SelectItem value="Sales Rep">Sales Rep</SelectItem>
                      <SelectItem value="Operations">Operations</SelectItem>
                      <SelectItem value="IT">IT</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    How did you hear about us? *
                  </Label>
                  <Select
                    value={formData.referral_source}
                    onValueChange={(v) => handleChange("referral_source", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-referral-select"
                      className="mt-1.5 border-slate-300"
                    >
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Event">Event</SelectItem>
                      <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                      <SelectItem value="Google">Google</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="other_info" className="text-sm font-medium text-slate-700">
                  Additional Information
                </Label>
                <Textarea
                  id="other_info"
                  data-testid="contact-other-info"
                  value={formData.other_info}
                  onChange={(e) => handleChange("other_info", e.target.value)}
                  placeholder="Tell us about your business and what you're looking for..."
                  rows={4}
                  className="mt-1.5 border-slate-300 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-6 w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full h-12 text-sm font-semibold"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" /> Schedule Your Personalized Demo
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
